import React, { createContext, useContext, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import api from "@/lib/api";
import { AuthProvider, useAuth } from "@/context/AuthContext";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Banner from "@/components/Banner";
import Home from "@/pages/Home";
import Opportunities from "@/pages/Opportunities";
import OpportunityDetail from "@/pages/OpportunityDetail";
import BespokeSourcing from "@/pages/BespokeSourcing";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminDashboard from "@/pages/admin/AdminDashboard";

const SettingsContext = createContext(null);
export const useSettings = () => useContext(SettingsContext);

function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const refresh = () => api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  useEffect(() => { refresh(); }, []);
  return <SettingsContext.Provider value={{ settings, refresh }}>{children}</SettingsContext.Provider>;
}

function PublicLayout({ children }) {
  const { settings } = useSettings();
  return (
    <>
      <Navbar />
      <main>{children}</main>
      <Footer settings={settings} />
      <Banner banner={settings?.banner} />
    </>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex min-h-screen items-center justify-center bg-obsidian text-gold font-serif">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <SettingsProvider>
          <BrowserRouter>
            <Toaster position="top-center" theme="dark" richColors />
            <Routes>
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/opportunities" element={<PublicLayout><Opportunities /></PublicLayout>} />
              <Route path="/opportunities/:slug" element={<PublicLayout><OpportunityDetail /></PublicLayout>} />
              <Route path="/bespoke-sourcing" element={<PublicLayout><BespokeSourcing /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
              <Route path="/privacy" element={<PublicLayout><Legal type="privacy" /></PublicLayout>} />
              <Route path="/terms" element={<PublicLayout><Legal type="terms" /></PublicLayout>} />
              <Route path="/complaints" element={<PublicLayout><Legal type="complaints" /></PublicLayout>} />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </SettingsProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
